import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Template } from '@feathr/blackbox';
import { EmptyState } from '@feathr/components';
import TemplateMergefieldDefaults from '@feathr/extender/App/TemplatePage/TemplateMetadataForm/TemplateMergefieldDefaults';

export interface IMailrailMergeDataInputsProps {
  /** The resolved template for the mailing, supplied by the composer. */
  template?: Template;
  disabled?: boolean;
}

/**
 * The drip-builder "Default Merge Data" section for a Mailrail mailing. Mirrors the legacy
 * {@link MergeDataInputs} (drip-context merge-field defaults with an empty-state fallback), but
 * takes the resolved `template` directly — so it reflects the per-step template rather than the
 * campaign's.
 *
 * @param props - {@link IMailrailMergeDataInputsProps}.
 */
function MailrailMergeDataInputs({
  template,
  disabled = false,
}: Readonly<IMailrailMergeDataInputsProps>): JSX.Element {
  const { t } = useTranslation();

  return (
    <>
      <p>
        {t(
          'These values will be used to replace merge tags when the recipient does not have data for the corresponding field.',
        )}
      </p>
      {template && !!template.get('used_mergefields')?.length ? (
        <TemplateMergefieldDefaults
          context={'drip'}
          disabled={disabled || template.isReadOnly}
          template={template}
        />
      ) : (
        <EmptyState label={t('No merge tags to display')} />
      )}
    </>
  );
}

export default observer(MailrailMergeDataInputs);
